import type { AuthService as PwAuthService } from "@pw-tech/web-sdk";
import { fetchActiveSubscriptionStatus } from "@/lib/api/active-subscription";
import {
  ensureSignupCohortAssigned,
  SIGNUP_COHORT_ID,
} from "@/lib/api/cohort";
import { buildCheckoutUrl, fetchDefaultCheckoutUrl } from "@/lib/api/pricing-plans";

declare global {
  interface Window {
    initPWAuthWebSDK?: (config: Record<string, unknown>) => void;
  }
}

const AUTH_STYLE_ID = "pw-auth-web-sdk-style";
const AUTH_LOADER_TIMEOUT_MS = 15000;
const PI_APP_PATH = "/study-v2/pi";
const SESSION_COOKIE = "TOKEN_CONTEXT";

/** Element the SDK renders the modal into; empty until the flow opens. */
export const AUTH_MOUNT_ID = "pw_auth-flow";

/**
 * The SDK writes this cookie on login (it matches the `contextIdentifier` we pass), and it is
 * readable as soon as the modal closes — `sdk.isLoggedIn` still reports the pre-login value there.
 */
export function getSessionContext() {
  const entry = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${SESSION_COOKIE}=`));

  if (!entry) {
    return undefined;
  }

  try {
    const { token, randomId } = JSON.parse(
      decodeURIComponent(entry.slice(SESSION_COOKIE.length + 1)),
    );

    return typeof token === "string" && token
      ? { token, randomId: typeof randomId === "string" ? randomId : undefined }
      : undefined;
  } catch {
    return undefined;
  }
}

export function getSessionToken() {
  return getSessionContext()?.token;
}

/**
 * Sends a signed-in visitor to the Pi home if their subscription is active, or to the subscription
 * page otherwise — `checkoutUrl` overrides that fallback so a pricing CTA keeps the plan the
 * visitor picked, and `preselectPlan` decides whether the rest fall back to the base plan or to the
 * bare subscription page. Returns false when there is no session yet, so the caller can open login.
 */
export async function goToPiAppIfSignedIn(
  checkoutUrl?: string,
  preselectPlan = true,
) {
  const baseUrl = process.env.NEXT_PUBLIC_PW_URL;

  if (!baseUrl || !getSessionToken()) {
    return false;
  }

  const [subscription] = await Promise.all([
    fetchActiveSubscriptionStatus(),
    // Without a cohort, study-v2 bounces the visitor to its cohort-selection page.
    ensureSignupCohortAssigned(),
  ]);

  let destination = `${baseUrl.replace(/\/$/, "")}${PI_APP_PATH}`;

  if (!subscription.isActive) {
    destination =
      checkoutUrl ??
      (preselectPlan ? await fetchDefaultCheckoutUrl() : buildCheckoutUrl());
  }

  window.location.href = destination;

  return true;
}

/**
 * The SDK stylesheet ships an unlayered `*{margin:0;padding:0}` reset. Unlayered rules beat every
 * layered rule, so it would flatten all Tailwind v4 spacing. Claiming the loader's style id first
 * lets us pull the same CSS in via the low-priority `pw-auth` layer instead.
 */
function reserveScopedStyles(loaderUrl: string) {
  if (document.getElementById(AUTH_STYLE_ID)) {
    return;
  }

  let cssHref: string;
  try {
    const url = new URL(loaderUrl, window.location.href);
    const version = url.searchParams.get("version") ?? "";
    if (!/^\d+\.\d+\.\d+$/.test(version)) {
      return;
    }
    const base = url.href.split("?")[0].replace(/\/[^/]+$/, "");
    cssHref = `${base}/v${version}/pw-auth-web-sdk.style.css`;
  } catch {
    return;
  }

  const style = document.createElement("style");
  style.id = AUTH_STYLE_ID;
  style.textContent = `@import url("${cssHref}") layer(pw-auth);`;
  document.head.appendChild(style);
}

/** Fallback for when the version can't be derived and the loader injects its own unlayered <link>. */
function demoteInjectedStylesheet() {
  const link = document.querySelector<HTMLLinkElement>(`link#${AUTH_STYLE_ID}`);
  if (!link) {
    return;
  }

  const style = document.createElement("style");
  style.id = AUTH_STYLE_ID;
  style.textContent = `@import url("${link.href}") layer(pw-auth);`;
  link.replaceWith(style);
}

let webSdkPromise: Promise<PwAuthService | undefined> | undefined;

async function initWebSdk(): Promise<PwAuthService | undefined> {
  const organizationId = process.env.NEXT_PUBLIC_PW_ORG_ID;
  const apiBaseUrl = process.env.NEXT_PUBLIC_PW_API_BASE_URL;
  const clientId = process.env.NEXT_PUBLIC_PW_CLIENT_ID;
  const clientSecret = process.env.NEXT_PUBLIC_PW_CLIENT_SECRET;

  if (!organizationId || !apiBaseUrl || !clientId || !clientSecret) {
    console.warn(
      "[auth] PW web SDK env vars missing — opening auth modal without a webSDK instance.",
    );
    return undefined;
  }

  const { AuthService, EVENT_PLATFORMS, EVENTS_ORG, LoginMethods } = await import(
    "@pw-tech/web-sdk"
  );

  return AuthService.getInstance({
    organizationId,
    apiBaseUrl,
    clientId,
    clientSecret,
    loginMethods: [LoginMethods.OTP, LoginMethods.PASSWORD],
    // Gives the instance an `eventService`, which lib/analytics uses as its GA transport.
    eventConfig: {
      eventPlatforms: [EVENT_PLATFORMS.GA],
      organizationName: EVENTS_ORG.PHYSICS_WALLAH,
      eventsDebugMode: false,
    },
    contextIdentifier: "TOKEN_CONTEXT",
    loginPageUrl: window.location.origin,
    exposeToWindow: true,
    localStorageFallback: true,
    debugMode: false,
  });
}

export function getWebSdk() {
  webSdkPromise ??= initWebSdk().catch((error) => {
    console.error("[auth] Unable to initialise PW web SDK", error);
    return undefined;
  });

  return webSdkPromise;
}

function waitForAuthInit() {
  return new Promise<void>((resolve, reject) => {
    if (typeof window.initPWAuthWebSDK === "function") {
      resolve();
      return;
    }

    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (typeof window.initPWAuthWebSDK === "function") {
        window.clearInterval(timer);
        resolve();
      } else if (Date.now() - startedAt >= AUTH_LOADER_TIMEOUT_MS) {
        window.clearInterval(timer);
        reject(new Error("PW auth modal failed to load"));
      }
    }, 50);
  });
}

async function ensureAuthLoader() {
  const scriptSrc = process.env.NEXT_PUBLIC_AUTH_SDK;

  if (!scriptSrc) {
    throw new Error(
      "NEXT_PUBLIC_AUTH_SDK is missing. Set it to the PW auth loader URL before using login.",
    );
  }

  if (typeof window.initPWAuthWebSDK === "function") {
    return window.initPWAuthWebSDK;
  }

  reserveScopedStyles(scriptSrc);

  if (!document.querySelector('script[data-pw-auth-loader="true"]')) {
    const script = document.createElement("script");
    script.src = scriptSrc;
    script.defer = true;
    script.dataset.pwAuthLoader = "true";
    document.head.appendChild(script);
  }

  await waitForAuthInit();
  demoteInjectedStylesheet();

  return window.initPWAuthWebSDK!;
}

/** Automated test runs skip the captcha challenge. */
function isAutomation() {
  return (
    window.navigator.webdriver === true &&
    sessionStorage.getItem("isAutomation") === "true"
  );
}

function getCaptchaConfig() {
  const siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_CAPTCHA_SITE_KEY;

  return {
    siteKey,
    showCaptcha: Boolean(siteKey) && !isAutomation(),
    fallbackSiteKey:
      process.env.NEXT_PUBLIC_CLOUDFLARE_CAPTCHA_SITE_KEY_FALLBACK,
  };
}

export async function openAuthModal(
  onClose: () => void,
  cohortRedirectUrl?: string,
) {
  const isModalOpen = Boolean(
    document.getElementById(AUTH_MOUNT_ID)?.children.length,
  );

  if (isModalOpen) {
    return;
  }

  const [webSDK, initializeAuthModal] = await Promise.all([
    getWebSdk(),
    ensureAuthLoader(),
  ]);

  const baseUrl = process.env.NEXT_PUBLIC_PW_URL ?? "";

  initializeAuthModal({
    flow: "auth",
    renderType: "modal",
    captchaConfig: getCaptchaConfig(),
    ...(webSDK ? { webSDK } : {}),
    firebaseRemoteConfig: {
      enable_v2_auth_flow: true,
    },
    logFirebaseEvent: () => {},
    shouldModalCloseImmediately: true,
    // New registrations are created straight into the Pi cohort, so the SDK skips its picker.
    skipCohortSelection: true,
    ...(SIGNUP_COHORT_ID ? { cohortId: SIGNUP_COHORT_ID } : {}),
    details: {
      pageName: "pi-landing-page",
      pageReferrer: document.referrer,
      pageReferrerUrl: document.referrer,
      parentURL: window.location.origin,
      skipCohortSelection: true,
      // Hard override: if the SDK still takes its cohort branch, keep the user inside Pi.
      cohortRedirectionURL:
        cohortRedirectUrl ?? `${baseUrl.replace(/\/$/, "")}${PI_APP_PATH}`,
      navigationType: "replace",
      // Fires on every close, successful or not; `shouldModalCloseImmediately` is what enables it.
      onCloseModalCallBack: onClose,
    },
    ctaName: "login",
    // Keeps the SDK from routing newly registered users into PW onboarding/cohort selection.
    cameFrom: "blog",
  });
}
